import { useQuery } from '@tanstack/react-query';
import type { AssistantCapabilitiesResponse } from '@app-types/assistant.ts';
import * as assistantApi from '../api/assistant';
import { useAssistantPageContextState } from '../context';
import { useAppSelector } from '@store/store';
import {
  selectCurrentDomain,
  selectSiteSettings,
} from '@store/slices/settingsSlice';
import { selectProfile } from '@store/slices/userSlice';

export interface UseAssistantCapabilitiesOptions {
  enabled?: boolean;
}

export interface UseAssistantCapabilitiesReturn {
  capabilities: AssistantCapabilitiesResponse | null;
  loading: boolean;
  error: unknown;
}

export const useAssistantCapabilities = (
  options: UseAssistantCapabilitiesOptions = {}
): UseAssistantCapabilitiesReturn => {
  const { effectiveContext } = useAssistantPageContextState();
  const profile = useAppSelector(selectProfile);
  const authenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const site = useAppSelector(selectSiteSettings);
  const currentDomain = useAppSelector(selectCurrentDomain);
  const enabled = options.enabled ?? true;
  const siteScope = site?.site_code || currentDomain || window.location.host;
  const actorScope =
    authenticated && profile?.id ? `user:${profile.id}` : 'anonymous';

  const query = useQuery({
    queryKey: [
      'assistant',
      'capabilities',
      siteScope,
      actorScope,
      effectiveContext,
    ],
    queryFn: () =>
      assistantApi.getCapabilities({
        version: 2,
        context: effectiveContext,
      }),
    enabled,
    staleTime: 60000,
    placeholderData: undefined,
    retry: false,
  });

  return {
    capabilities: query.data ?? null,
    loading: query.isLoading,
    error: query.error,
  };
};
